/**
 * Headless playback driver for the editor V2 rewrite.
 *
 * Ported from the `VideoEditor.tsx` monolith's rAF playback effect. The
 * monolith advanced `currentTime` off the live `<video>` element's
 * `currentTime` (source of truth for actual decode/playback rate); this
 * hook has no DOM/video ref (hooks-only, no UI), so it advances
 * `currentTime` by wall-clock delta between frames instead. The preview
 * component (built in a later task) owns the `<video>` element and syncs
 * its `currentTime` off the store's `currentTime` separately.
 *
 * Clip-boundary logic: as the playhead crosses into the next clip, the
 * store's `selectedClipId` is advanced to match (mirrors the monolith's
 * `setSelectedClipId(nextClip.id)`), without going through the `selectClip`
 * action — that action resets `currentTime`/`isPlaying`, which would fight
 * the running loop.
 */
'use client';

import { useEffect, useRef } from 'react';
import { selectActiveClip, selectDuration, useEditorStore, type EditorState } from '@/lib/editor/editorStore';

export function useEditorPlayback(): void {
  const isPlaying = useEditorStore((state) => state.isPlaying);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }

    let lastTimestamp: number | null = null;

    const stop = (patch: Partial<EditorState>) => {
      useEditorStore.setState(patch);
      rafRef.current = null;
    };

    const tick = (timestamp: number) => {
      const state = useEditorStore.getState();
      if (!state.isPlaying) {
        rafRef.current = null;
        return;
      }

      const duration = selectDuration(state);
      if (duration <= 0) {
        // Nothing to play — stop rather than spin.
        stop({ isPlaying: false });
        return;
      }

      const delta = lastTimestamp === null ? 0 : (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;
      const nextTime = state.currentTime + delta;

      if (nextTime >= duration - 0.02) {
        stop({ currentTime: duration, isPlaying: false });
        return;
      }

      const activeClip = selectActiveClip(state, nextTime);
      const patch: Partial<EditorState> = { currentTime: nextTime };
      if (activeClip && activeClip.id !== state.selectedClipId) {
        patch.selectedClipId = activeClip.id;
      }
      useEditorStore.setState(patch);

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [isPlaying]);
}
